/**
 * Checks if an age range represents a Baby Play Kit (Kits 1-6, up to 12 months).
 * Uses a locale-agnostic approach by extracting the maximum numeric value.
 *
 * Baby kits include:
 * - Week-based ranges: 0-12 weeks (max value ≤12)
 * - Month-based ranges: 3-4, 5-6, 7-8, 9-10, 11-12 (max value ≤12)
 *
 * Toddler kits start at month 13+ (max value ≥13)
 *
 * @param ageRange - Age range string in any locale (e.g., "Weeks 0-12", "Monat 3-4", "5-6 maanden")
 * @returns true if the age range is for a Baby kit (max value ≤12), false otherwise
 *
 * @example
 * checkIsBabyAgeRange('Weeks 0-12') // true (max=12)
 * checkIsBabyAgeRange('5-6 maanden') // true (max=6)
 * checkIsBabyAgeRange('Months 13-15') // false (max=15)
 */
export const checkIsBabyAgeRange = (ageRange?: string | null): boolean => {
  if (!ageRange?.trim()) return false;

  // Extract all numbers from the age range string
  const numbers = ageRange.match(/\d+/g);
  if (!numbers || numbers.length === 0) return false;

  // Get the maximum number - this represents the upper bound of the age range
  // Using reduce instead of Math.max(...spread) to avoid potential stack overflow with large arrays
  const maxAge = numbers.reduce((max, num) => {
    const parsed = Number(num);
    return parsed > max ? parsed : max;
  }, 0);

  // Baby kits have max age ≤12 (months or weeks)
  // Toddler kits start at 13+ months
  return maxAge <= 12;
};
